<template>
	<div>
		<PageHeader
			:data="{
				name: 'Power Almanac Import',
				breadcrumbs: [
					{ name: 'Home', to: '/' },
					{ name: 'Contacts', to: '/contacts' },
				],
			}"
		/>

		<div class="row">
			<!-- Upload Section -->
			<div class="col-12 mb-3">
				<div class="card">
					<div class="card-body">
						<h4 class="mb-3">Upload Power Almanac CSV</h4>
						<div class="row">
							<div class="col-md-6">
								<div class="form-group mb-3">
									<label for="file">Choose Power Almanac CSV file</label>
									<input
										class="form-control"
										type="file"
										accept=".csv"
										@change="uploadAlmanacFile($event)"
										:disabled="loading || applying"
									/>
									<small class="form-text text-muted">
										Weekly Power Almanac export file for municipality and contact updates
									</small>
								</div>
								<div v-if="uploading" class="alert alert-info">
									<span class="spinner-border spinner-border-sm me-2"></span>
									Uploading and analyzing file...
								</div>
								<div v-if="uploadSuccess && !matchingComplete" class="alert alert-success">
									<i class="fas fa-check-circle me-2"></i>
									File uploaded successfully! {{ totalRecords }} records found.
								</div>
								<div v-if="loading" class="alert alert-info">
									<div class="d-flex align-items-center mb-2">
										<span class="spinner-border spinner-border-sm me-2"></span>
										<strong>Processing {{ totalRecords }} records...</strong>
										<span class="ms-auto badge bg-primary">{{ formatTime(processingTime) }}</span>
									</div>
									<div class="progress" style="height: 25px;">
										<div
											class="progress-bar progress-bar-striped progress-bar-animated bg-info"
											role="progressbar"
											:style="`width: ${estimatedProgress}%`"
										>
											{{ estimatedProgress }}% (Estimated)
										</div>
									</div>
									<small class="text-muted mt-2 d-block">
										<i class="fas fa-info-circle me-1"></i>
										Processing large files can take 3-5 minutes. Estimated time remaining: {{ getEstimatedTimeRemaining() }}
									</small>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			<!-- Import History - Always Visible -->
			<div class="col-12 mb-3">
				<div class="card">
					<div class="card-header d-flex align-items-center">
						<h5 class="mb-0">Import History</h5>
						<button class="btn btn-sm btn-primary ms-auto" @click="loadImportHistory" :disabled="applying">
							<i class="fas fa-sync"></i> Refresh
						</button>
					</div>
					<div class="card-body">
						<div v-if="loadingHistory" class="text-center py-4">
							<span class="spinner-border spinner-border-lg"></span>
							<p>Loading history...</p>
						</div>
						<div v-else-if="importHistory.length === 0" class="alert alert-info">No import history found.</div>
						<div v-else class="table-responsive">
							<table class="table table-bordered table-hover">
								<thead>
									<tr>
										<th>Date</th>
										<th>Imported By</th>
										<th>Statistics</th>
										<th>Status</th>
										<th>Actions</th>
									</tr>
								</thead>
								<tbody>
									<tr v-for="history in importHistory" :key="history.id">
										<td>{{ formatDate(history.import_date) }}</td>
										<td>{{ history.user_name || 'Unknown User' }}</td>
										<td>
											<small>
												<div>Municipalities: {{ history.municipalities_updated }} updated</div>
												<div>
													Contacts: {{ history.contacts_created }} created, {{ history.contacts_updated }}
													updated
												</div>
											</small>
										</td>
										<td>
											<span class="badge" :class="getStatusBadge(history.status)">
												{{ history.status }}
											</span>
										</td>
										<td>
											<button
												v-if="history.status === 'completed'"
												class="btn btn-sm btn-warning"
												@click="revertImport(history.id)"
												:disabled="applying"
											>
												<i class="fas fa-undo"></i> Revert
											</button>
											<span v-else class="text-muted">—</span>
										</td>
									</tr>
								</tbody>
							</table>
						</div>
					</div>
				</div>
			</div>

			<!-- Matching Results -->
			<div v-if="matchingComplete" class="col-12">
				<!-- Summary Statistics -->
				<div class="card mb-3">
					<div class="card-body">
						<h4 class="mb-3">Import Summary</h4>
						<div class="row">
							<div class="col-md-3">
								<div class="card bg-primary text-white">
									<div class="card-body">
										<h5>{{ municipalityMatches.length }}</h5>
										<p class="mb-0">Municipality Updates</p>
									</div>
								</div>
							</div>
							<div class="col-md-3">
								<div class="card bg-success text-white">
									<div class="card-body">
										<h5>{{ newContacts.length }}</h5>
										<p class="mb-0">New Contacts</p>
									</div>
								</div>
							</div>
							<div class="col-md-3">
								<div class="card bg-info text-white">
									<div class="card-body">
										<h5>{{ contactUpdates.length }}</h5>
										<p class="mb-0">Contact Updates</p>
									</div>
								</div>
							</div>
							<div class="col-md-3">
								<div class="card bg-warning text-white">
									<div class="card-body">
										<h5>{{ duplicateEmails.length }}</h5>
										<p class="mb-0">Duplicate Emails</p>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>

				<!-- Duplicate Emails Resolution -->
				<div v-if="duplicateEmails.length > 0" class="card mb-3">
					<div class="card-header bg-warning">
						<h5 class="mb-0">
							<i class="fas fa-exclamation-triangle me-2"></i>
							Duplicate Emails Detected ({{ duplicateEmails.length }})
						</h5>
						<small class="text-muted">
							Multiple contacts with the same email were found. Please select which record to keep for each duplicate.
						</small>
					</div>
					<div class="card-body">
						<div v-for="(dup, dupIndex) in visibleDuplicateEmails" :key="'dup-' + dupIndex" class="mb-4 border p-3 rounded">
							<div class="mb-2">
								<strong>Municipality:</strong> {{ dup.municipality_name }} ({{ dup.municipality_zip }})
								<br/>
								<strong>Duplicate Email:</strong> {{ dup.email }}
							</div>
							<div class="table-responsive">
								<table class="table table-sm table-bordered">
									<thead>
										<tr>
											<th width="50">Select</th>
											<th>Name</th>
											<th>Title</th>
											<th>Phone</th>
										</tr>
									</thead>
									<tbody>
										<tr
											v-for="(contact, contactIndex) in dup.contacts"
											:key="'dup-' + dupIndex + '-contact-' + contactIndex"
											:class="{ 'table-success': dup.selected_index === contactIndex }"
										>
											<td class="text-center">
												<input
													type="radio"
													:name="'duplicate-' + dupIndex"
													:value="contactIndex"
													v-model="dup.selected_index"
													:disabled="applying"
												/>
											</td>
											<td>{{ contact.name }}</td>
											<td>{{ contact.title }}</td>
											<td>{{ contact.phone || '—' }}</td>
										</tr>
									</tbody>
								</table>
							</div>
						</div>

						<!-- Load More Button -->
						<div v-if="hasMoreDuplicates" class="text-center">
							<button class="btn btn-outline-warning" @click="loadMoreDuplicates" :disabled="applying">
								<i class="fas fa-chevron-down me-2"></i>
								Load More ({{ duplicateEmails.length - duplicateEmailsVisible }} remaining)
							</button>
						</div>
					</div>
				</div>

				<!-- Tabs for different sections -->
				<div class="card">
					<div class="card-header">
						<ul class="nav nav-tabs card-header-tabs" role="tablist">
							<li class="nav-item">
								<a
									class="nav-link"
									:class="{ active: activeTab === 'municipalities' }"
									@click.prevent="activeTab = 'municipalities'"
								>
									Municipality Updates ({{ municipalityMatches.length }})
								</a>
							</li>
							<li class="nav-item">
								<a
									class="nav-link"
									:class="{ active: activeTab === 'new-contacts' }"
									@click.prevent="activeTab = 'new-contacts'"
								>
									New Contacts ({{ newContacts.length }})
								</a>
							</li>
							<li class="nav-item">
								<a
									class="nav-link"
									:class="{ active: activeTab === 'contact-updates' }"
									@click.prevent="activeTab = 'contact-updates'"
								>
									Contact Updates ({{ contactUpdates.length }})
								</a>
							</li>
						</ul>
					</div>

					<div class="card-body">
						<!-- Municipality Updates Tab -->
						<div v-if="activeTab === 'municipalities'">
							<div class="mb-3">
								<button class="btn btn-sm btn-success me-2" @click="selectAllMunicipalities(true)" :disabled="applying">
									Select All
								</button>
								<button class="btn btn-sm btn-secondary" @click="selectAllMunicipalities(false)" :disabled="applying">
									Deselect All
								</button>
							</div>
							<div v-if="municipalityMatches.length === 0" class="alert alert-info">
								No municipality updates found.
							</div>
							<div v-else class="table-responsive">
								<table class="table table-bordered table-sm">
									<thead>
										<tr>
											<th width="50">Apply</th>
											<th>Municipality</th>
											<th>Match %</th>
											<th>Changes</th>
										</tr>
									</thead>
									<tbody>
										<tr v-for="(match, index) in municipalityMatches" :key="'muni-' + index">
											<td class="text-center">
												<input type="checkbox" v-model="match.apply" :disabled="applying" />
											</td>
											<td>
												<strong>{{ match.match.municipality.full_name }}</strong>
												<br />
												<small class="text-muted">
													{{ match.match.municipality.city }}, {{ match.match.municipality.state }}
													{{ match.match.municipality.zip }}
												</small>
											</td>
											<td>
												<span class="badge" :class="getConfidenceBadge(match.match_confidence)">
													{{ match.match_confidence }}%
												</span>
											</td>
											<td>
												<div v-for="(change, field) in match.changes" :key="field" class="mb-2">
													<strong>{{ formatFieldName(field) }}:</strong>
													<br />
													<span class="text-danger">
														<del>{{ change.old || '(empty)' }}</del>
													</span>
													<br />
													<span class="text-success">{{ change.new }}</span>
												</div>
											</td>
										</tr>
									</tbody>
								</table>
							</div>
						</div>

						<!-- New Contacts Tab -->
						<div v-if="activeTab === 'new-contacts'">
							<div class="mb-3">
								<button class="btn btn-sm btn-success me-2" @click="selectAllNewContacts(true)" :disabled="applying">
									Select All
								</button>
								<button class="btn btn-sm btn-secondary" @click="selectAllNewContacts(false)" :disabled="applying">
									Deselect All
								</button>
							</div>
							<div v-if="newContacts.length === 0" class="alert alert-info">No new contacts found.</div>
							<div v-else class="table-responsive">
								<table class="table table-bordered table-sm">
									<thead>
										<tr>
											<th width="50">Add</th>
											<th>Municipality</th>
											<th>Contact Details</th>
										</tr>
									</thead>
									<tbody>
										<tr v-for="(contact, index) in newContacts" :key="'new-' + index">
											<td class="text-center">
												<input type="checkbox" v-model="contact.apply" :disabled="applying" />
											</td>
											<td>
												<strong>{{ contact.municipality_name }}</strong>
											</td>
											<td>
												<div><strong>Name:</strong> {{ contact.match.name }}</div>
												<div><strong>Title:</strong> {{ contact.match.title }}</div>
												<div><strong>Email:</strong> {{ contact.match.email }}</div>
												<div v-if="contact.match.phone">
													<strong>Phone:</strong> {{ contact.match.phone }}
												</div>
											</td>
										</tr>
									</tbody>
								</table>
							</div>
						</div>

						<!-- Contact Updates Tab -->
						<div v-if="activeTab === 'contact-updates'">
							<div class="mb-3">
								<button class="btn btn-sm btn-success me-2" @click="selectAllContactUpdates(true)" :disabled="applying">
									Select All
								</button>
								<button class="btn btn-sm btn-secondary" @click="selectAllContactUpdates(false)" :disabled="applying">
									Deselect All
								</button>
							</div>
							<div v-if="contactUpdates.length === 0" class="alert alert-info">No contact updates found.</div>
							<div v-else class="table-responsive">
								<table class="table table-bordered table-sm">
									<thead>
										<tr>
											<th width="50">Apply</th>
											<th>Contact</th>
											<th>Municipality</th>
											<th>Changes</th>
										</tr>
									</thead>
									<tbody>
										<tr v-for="(contact, index) in contactUpdates" :key="'update-' + index">
											<td class="text-center">
												<input type="checkbox" v-model="contact.apply" :disabled="applying" />
											</td>
											<td>
												<strong>{{ contact.match.name }}</strong>
												<br />
												<small class="text-muted">{{ contact.match.email }}</small>
											</td>
											<td>
												<strong>{{ contact.municipality_name }}</strong>
											</td>
											<td>
												<div v-for="(change, field) in contact.changes" :key="field" class="mb-2">
													<strong>{{ formatFieldName(field) }}:</strong>
													<br />
													<span class="text-danger">
														<del>{{ change.old || '(empty)' }}</del>
													</span>
													<br />
													<span class="text-success">{{ change.new }}</span>
												</div>
											</td>
										</tr>
									</tbody>
								</table>
							</div>
						</div>

					</div>

					<!-- Apply Changes Button -->
					<div class="card-footer">
						<button class="btn btn-primary btn-lg" @click="applyChanges" :disabled="applying">
							<span v-if="applying">
								<span class="spinner-border spinner-border-sm me-2"></span>
								Applying Changes...
							</span>
							<span v-else>
								<i class="fas fa-check"></i> Apply Selected Changes
							</span>
						</button>
						<small class="text-muted ms-3"> Selected: {{ selectedCount }} changes </small>
					</div>
				</div>
			</div>
		</div>

		<!-- Blocking Overlay During Apply -->
		<div v-if="applying" class="blocking-overlay">
			<div class="overlay-content">
				<div class="spinner-border text-light" style="width: 3rem; height: 3rem;" role="status"></div>
				<h4 class="text-white mt-3">Applying Changes...</h4>
				<p class="text-white-50">Please wait, do not close or refresh this page.</p>
			</div>
		</div>
	</div>
</template>

<script>
import PageHeader from '@/components/layout/PageHeader'
import { handleErrors } from '../../actions/helpers'
import restApi from '../../api/index.js'

export default {
	name: 'ContactsImport',
	components: { PageHeader },
	data() {
		return {
			uploading: false,
			loading: false,
			applying: false,
			loadingHistory: false,
			uploadSuccess: false,
			matchingComplete: false,
			totalRecords: 0,
			csvPath: '',
			activeTab: 'municipalities',
			municipalityMatches: [],
			contactMatches: [],
			unmatchedMunicipalities: [],
			duplicateEmails: [],
			duplicateEmailsVisible: 5,
			importHistory: [],
			processingTime: 0,
			processingTimer: null,
			estimatedProgress: 0,
			progressTimer: null,
		}
	},
	computed: {
		newContacts() {
			return this.contactMatches.filter(c => c.is_new)
		},
		contactUpdates() {
			return this.contactMatches.filter(c => !c.is_new)
		},
		selectedCount() {
			const selectedMuni = this.municipalityMatches.filter(m => m.apply).length
			const selectedContacts = this.contactMatches.filter(c => c.apply).length
			return selectedMuni + selectedContacts
		},
		visibleDuplicateEmails() {
			return this.duplicateEmails.slice(0, this.duplicateEmailsVisible)
		},
		hasMoreDuplicates() {
			return this.duplicateEmailsVisible < this.duplicateEmails.length
		},
	},
	mounted() {
		this.loadImportHistory()
	},
	methods: {
		uploadAlmanacFile($event) {
			this.uploading = true
			this.uploadSuccess = false
			this.matchingComplete = false
			this.loading = false
			this.duplicateEmailsVisible = 5

			const form = new FormData()
			form.append('file', $event.target.files[0])

			const config = {
				headers: { 'content-type': 'multipart/form-data' },
			}

			restApi
				.post('/almanac/upload', form, config)
				.then(({ data }) => {
					this.csvPath = data.path
					this.totalRecords = data.total_records
					this.uploadSuccess = true
					this.uploading = false

					// Show success message briefly before starting matching
					setTimeout(() => {
						this.processMatching()
					}, 500)
				})
				.catch(error => {
					this.uploading = false
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, 'Upload failed'),
						timer: 6000,
					})
				})
		},

		processMatching() {
			this.loading = true
			this.processingTime = 0
			this.estimatedProgress = 0

			// Estimate: ~0.08 seconds per record (based on 2634 records in 3.5 minutes)
			const estimatedTotalTime = this.totalRecords * 0.08

			// Start timers
			this.processingTimer = setInterval(() => {
				this.processingTime++
				// Update estimated progress based on elapsed time
				this.estimatedProgress = Math.min(95, Math.floor((this.processingTime / estimatedTotalTime) * 100))
			}, 1000)

			restApi
				.post('/almanac/process-matching', { csvPath: this.csvPath }, {
					timeout: 300000 // 5 minutes timeout for large files
				})
				.then(({ data }) => {
					// Stop timers
					if (this.processingTimer) {
						clearInterval(this.processingTimer)
						this.processingTimer = null
					}

					this.estimatedProgress = 100

					this.municipalityMatches = data.municipalities.map(m => ({ ...m, apply: true }))
					this.contactMatches = data.contacts.map(c => ({ ...c, apply: true }))
					this.unmatchedMunicipalities = data.unmatched_municipalities
					this.duplicateEmails = data.duplicate_emails || []

					this.matchingComplete = true
					this.loading = false

					const duplicateWarning = this.duplicateEmails.length > 0
						? `<br><span class="text-warning"><i class="fas fa-exclamation-triangle me-1"></i><strong>${this.duplicateEmails.length}</strong> duplicate email groups found - please review!</span>`
						: ''

					this.$swal.fire({
						icon: this.duplicateEmails.length > 0 ? 'warning' : 'success',
						title: 'Matching complete!',
						html: `Found <strong>${this.municipalityMatches.length}</strong> municipality updates and <strong>${this.contactMatches.length}</strong> contact changes.${duplicateWarning}<br><small class="text-muted">Completed in ${this.formatTime(this.processingTime)}</small>`,
						timer: this.duplicateEmails.length > 0 ? 8000 : 5000,
					})
				})
				.catch(error => {
					// Stop timers
					if (this.processingTimer) {
						clearInterval(this.processingTimer)
						this.processingTimer = null
					}

					this.loading = false
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, 'Matching failed'),
						text: error.code === 'ECONNABORTED' ? 'The request timed out. The file may be too large.' : '',
						timer: 6000,
					})
				})
		},

		getEstimatedTimeRemaining() {
			const estimatedTotalTime = this.totalRecords * 0.08 // seconds
			const remaining = Math.max(0, estimatedTotalTime - this.processingTime)
			return this.formatTime(Math.floor(remaining))
		},

		formatTime(seconds) {
			const mins = Math.floor(seconds / 60)
			const secs = seconds % 60
			return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`
		},

		applyChanges() {
			const selectedMunicipalities = this.municipalityMatches.filter(m => m.apply)
			const selectedContacts = this.contactMatches.filter(c => c.apply)

			if (selectedMunicipalities.length === 0 && selectedContacts.length === 0) {
				this.$swal.fire({
					icon: 'warning',
					title: 'No changes selected',
					text: 'Please select at least one change to apply.',
				})
				return
			}

			this.$swal.fire({
				title: 'Confirm Import',
				html: `You are about to apply:<br><br>
					<strong>${selectedMunicipalities.length}</strong> municipality updates<br>
					<strong>${selectedContacts.length}</strong> contact changes<br><br>
					This action can be reverted later from the Import History tab.`,
				icon: 'question',
				showCancelButton: true,
				confirmButtonText: 'Yes, Apply Changes',
				cancelButtonText: 'Cancel',
				confirmButtonColor: '#28a745',
				cancelButtonColor: '#6c757d',
				allowOutsideClick: false,
				allowEscapeKey: false,
				// Override global toast settings
				toast: false,
				position: 'center',
				timer: null,
				showConfirmButton: true,
			}).then(result => {
				if (result.isConfirmed) {
					this.performApply(selectedMunicipalities, selectedContacts)
				}
			})
		},

		performApply(selectedMunicipalities, selectedContacts) {
			this.applying = true

			// Build a set of duplicate emails that should be excluded (non-selected duplicates)
			// Use email+name as unique key since duplicates are within same municipality
			const excludedDuplicateEmails = new Set()
			this.duplicateEmails.forEach(dup => {
				dup.contacts.forEach((contact, index) => {
					if (index !== dup.selected_index) {
						// This contact was not selected, exclude by email+name
						const key = `${contact.email}|${contact.name}`
						excludedDuplicateEmails.add(key)
					}
				})
			})

			// Filter out non-selected duplicate contacts
			const filteredContacts = selectedContacts.filter(c => {
				const contactEmail = c.match.email || ''
				const contactName = c.match.name || ''
				const key = `${contactEmail}|${contactName}`
				return !excludedDuplicateEmails.has(key)
			})

			const municipalityChanges = selectedMunicipalities.map(m => ({
				apply: true,
				municipality_id: m.match.municipality.id,
				fields: Object.keys(m.changes).reduce((acc, field) => {
					acc[field] = m.changes[field].new
					return acc
				}, {}),
			}))

			const contactChanges = filteredContacts.map(c => ({
				apply: true,
				is_new: c.is_new,
				contact_id: c.match.id || null,
				fields: c.is_new
					? {
							municipality_id: c.municipality_id,
							name: c.match.name,
							first_name: c.match.first_name,
							last_name: c.match.last_name,
							email: c.match.email,
							title: c.match.title,
							phone: c.match.phone,
							status: 'active',
							source: 'power-almanac',
						}
					: Object.keys(c.changes).reduce((acc, field) => {
							acc[field] = c.changes[field].new
							return acc
						}, {}),
			}))

			restApi
				.post('/almanac/apply-changes', {
					file_path: this.csvPath,
					municipality_changes: municipalityChanges,
					contact_changes: contactChanges,
				})
				.then(({ data }) => {
					this.applying = false

					this.$swal.fire({
						icon: 'success',
						title: 'Changes Applied!',
						html: `
							<strong>Successfully applied:</strong><br>
							${data.stats.municipalities_updated} municipality updates<br>
							${data.stats.contacts_created} new contacts<br>
							${data.stats.contacts_updated} contact updates
						`,
						timer: 6000,
					})

					// Reset for next import
					this.matchingComplete = false
					this.uploadSuccess = false
					this.csvPath = ''
					this.municipalityMatches = []
					this.contactMatches = []

					// Refresh history
					this.loadImportHistory()
				})
				.catch(error => {
					this.applying = false
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, 'Apply failed'),
						timer: 6000,
					})
				})
		},

		loadImportHistory() {
			this.loadingHistory = true

			restApi
				.get('/almanac/import-history')
				.then(({ data }) => {
					this.importHistory = data
					this.loadingHistory = false
				})
				.catch(error => {
					this.loadingHistory = false
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, 'Failed to load history'),
						timer: 6000,
					})
				})
		},

		revertImport(snapshotId) {
			this.$swal.fire({
				title: 'Revert Import?',
				text: 'This will restore all municipalities and contacts to their previous state before this import.',
				icon: 'warning',
				showCancelButton: true,
				confirmButtonText: 'Yes, Revert',
				cancelButtonText: 'Cancel',
				confirmButtonColor: '#dc3545',
				cancelButtonColor: '#6c757d',
				allowOutsideClick: false,
				allowEscapeKey: false,
				// Override global toast settings
				toast: false,
				position: 'center',
				timer: null,
				showConfirmButton: true,
			}).then(result => {
				if (result.isConfirmed) {
					// Show progress dialog
					this.$swal.fire({
						title: 'Reverting Import...',
						html: '<div class="text-center"><div class="spinner-border text-warning" role="status"></div><p class="mt-3">Please wait while we restore previous data...</p></div>',
						allowOutsideClick: false,
						allowEscapeKey: false,
						showConfirmButton: false,
						showCancelButton: false,
						toast: false,
						position: 'center',
					})

					restApi
						.post('/almanac/revert-import', { snapshot_id: snapshotId })
						.then(() => {
							// Load history first, then show success
							this.loadImportHistory()

							this.$swal.fire({
								icon: 'success',
								title: 'Import Reverted Successfully!',
								text: 'All changes have been rolled back to their previous state.',
								confirmButtonText: 'OK',
								// Override toast for success message - no timer, user must click OK
								toast: false,
								position: 'center',
								timer: null,
								showConfirmButton: true,
							})
						})
						.catch(error => {
							this.$swal.fire({
								icon: 'error',
								title: 'Revert Failed',
								text: handleErrors(error, 'An error occurred while reverting the import'),
								confirmButtonText: 'OK',
								// Override toast for error message - no timer, user must acknowledge
								toast: false,
								position: 'center',
								timer: null,
								showConfirmButton: true,
							})
						})
				}
			})
		},

		selectAllMunicipalities(value) {
			this.municipalityMatches.forEach(m => (m.apply = value))
		},

		selectAllNewContacts(value) {
			this.newContacts.forEach(c => (c.apply = value))
		},

		selectAllContactUpdates(value) {
			this.contactUpdates.forEach(c => (c.apply = value))
		},

		formatFieldName(field) {
			return field
				.replace(/_/g, ' ')
				.replace(/\b\w/g, l => l.toUpperCase())
		},

		formatDate(date) {
			return new Date(date).toLocaleString()
		},

		getConfidenceBadge(confidence) {
			if (confidence >= 95) return 'bg-success'
			if (confidence >= 85) return 'bg-info'
			if (confidence >= 75) return 'bg-warning'
			return 'bg-danger'
		},

		getStatusBadge(status) {
			if (status === 'completed') return 'bg-success'
			if (status === 'reverted') return 'bg-secondary'
			return 'bg-warning'
		},

		loadMoreDuplicates() {
			this.duplicateEmailsVisible += 5
		},
	},
}
</script>

<style scoped>
.table-responsive {
	max-height: 600px;
	overflow-y: auto;
}

.card-header-tabs .nav-link {
	cursor: pointer;
	pointer-events: auto;
}

.card-header-tabs .nav-link:hover {
	background-color: #f8f9fa;
}

.badge {
	font-size: 0.85rem;
}

.blocking-overlay {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background: rgba(0, 0, 0, 0.85);
	z-index: 9999;
	display: flex;
	align-items: center;
	justify-content: center;
}

.overlay-content {
	text-align: center;
	padding: 2rem;
}
</style>
